import { Box } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";

import { TILE_VARIANTS } from "./constants";
import type { Variant } from "./ParentTile";

export const OuterWrapper = styled("div", {
  shouldForwardProp: (prop) =>
    prop !== "variant" && prop !== "controlledSelected",
})<{
  variant: Variant;
  controlledSelected?: boolean;
}>(({ theme, variant, controlledSelected }) => ({
  position: "relative",
  width: TILE_VARIANTS[variant].width,
  height: TILE_VARIANTS[variant].height,
  "& > .parent-tile_link": {
    boxSizing: "content-box",
    display: "block",
    width: TILE_VARIANTS[variant].width + 8,
    height: TILE_VARIANTS[variant].height + 8,
    position: "absolute",
    top: -4,
    left: -4,
    zIndex: 1,
    transition: "transform 400ms cubic-bezier(.47,1.64,.41,.8)",
    clipPath: "polygon(-50% 50%, 50% 100%, 150% 50%, 50% 0)",
  },
  ...(controlledSelected === undefined
    ? {
        "& > .parent-tile_link__active .parent-tile_shape-stroke": {
          stroke:
            variant === "medium"
              ? theme.palette.primary.main
              : theme.palette.text.primary,
          strokeWidth: variant === "medium" ? "3.5px" : "2px",
        },
      }
    : {
        "& .parent-tile_shape-stroke": {
          transition: "stroke 0.15s ease, stroke-width 0.15s ease",
          stroke: controlledSelected
            ? variant === "medium"
              ? theme.palette.primary.main
              : theme.palette.text.primary
            : theme.palette.text.disabled,
          strokeWidth:
            controlledSelected && variant === "medium" ? "3.5px" : "2px",
        },
      }),
  "& > .parent-tile_link:hover, & > .parent-tile_link:focus-visible": {
    transform: "translateY(-16px)",
  },
  "& > .parent-tile_link:focus-visible": {
    outline: "none",
  },
}));

const BaseTileText = styled("span")({
  display: "block",
  position: "absolute",
  zIndex: 2,
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 120,
  textAlign: "center",
  fontStyle: "normal",
});

export const TileName = styled(BaseTileText, {
  shouldForwardProp: (prop) => prop !== "variant" && prop !== "nameLength",
})<{ variant: Variant; color: string; nameLength: number }>(({
  variant,
  color,
  nameLength,
}) => {
  const base = TILE_VARIANTS[variant].nameFontSize;
  const scale = Math.min(1, 17 / Math.max(nameLength, 1));

  return {
    top: "50%",
    fontSize: base * scale,
    lineHeight: "1.15",
    letterSpacing: "-0.2px",
    fontWeight: 600,
    color,
    whiteSpace: "pre-line",
  };
});

export const MotionTileName = motion.create(TileName);

export const TileStatus = styled(BaseTileText)<{
  variant: Variant;
  color: string;
}>(({ variant, color }) => ({
  top: "65%",
  fontSize: TILE_VARIANTS[variant].statusFontSize,
  lineHeight: "normal",
  letterSpacing: "-0.13px",
  fontWeight: 500,
  color,
}));

export const MotionTileStatus = motion.create(TileStatus);

export const TileIcon = styled(Box)(() => ({
  position: "absolute",
  zIndex: 3,
  left: "50%",
  top: "35%",
  transform: "translate(-50%, -50%)",
  pointerEvents: "none",
}));

export const TileLogo = styled(Box)(() => ({
  position: "absolute",
  zIndex: 3,
  left: "50%",
  top: "50%",
  transform: "translate(-50%, -50%)",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 87,
  height: 25,
  flexShrink: 0,
  aspectRatio: "87 / 25",
  "& img": {
    height: "100%",
    width: "100%",
    objectFit: "contain",
    display: "block",
    backgroundColor: "transparent",
  },
}));

export const MotionTileLogo = motion.create(TileLogo);

const draw = {
  hidden: { pathLength: 0, opacity: 0 },
  visible: (i: number) => {
    const delay = i * 0.05;
    return {
      pathLength: 1,
      opacity: 1,
      transition: {
        pathLength: {
          delay,
          type: "spring" as const,
          duration: 1.5,
          bounce: 0,
        },
        opacity: { delay, duration: 0.01 },
      },
    };
  },
};

export function ShadowShape({ variant = "large" }: { variant?: Variant }) {
  const theme = useTheme();
  const strokeColor = theme.palette.text.primary;
  const svgWidth = TILE_VARIANTS[variant].width;
  const svgHeight = TILE_VARIANTS[variant].height;
  return (
    <svg
      width="215"
      height="240"
      viewBox="0 0 215 240"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        width: svgWidth,
        height: svgHeight,
      }}
    >
      <motion.path
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 1.3 }}
        d="M98.9805 3.36719C104.277 0.309242 110.765 0.213996 116.136 3.08105L116.651 3.36719L204.296 53.9688C209.763 57.1254 213.132 62.9601 213.132 69.2734V170.477C213.132 176.593 209.971 182.259 204.803 185.477L204.296 185.78L116.651 236.382C111.355 239.44 104.867 239.535 99.4961 236.668L98.9805 236.382L11.3359 185.78C5.8685 182.624 2.50011 176.79 2.5 170.477V69.2734C2.5 63.1573 5.66071 57.4905 10.8291 54.2725L11.3359 53.9688L98.9805 3.36719Z"
        fill={strokeColor}
      />
    </svg>
  );
}

export function TileShape({
  bgColor = "#F5F2F8",
  bgImg,
  variant = "large",
  hideTopGlyph = false,
}: {
  bgColor?: string;
  bgImg?: string;
  variant?: Variant;
  hideTopGlyph?: boolean;
}) {
  const theme = useTheme();
  const outlineColor = theme.palette.text.primary;
  const strokeColor = bgImg
    ? theme.palette.primary.contrastText
    : theme.palette.text.primary;
  const svgWidth = TILE_VARIANTS[variant].width;
  const svgHeight = TILE_VARIANTS[variant].height;
  return (
    <motion.svg
      initial="hidden"
      animate="visible"
      width="215"
      height="240"
      viewBox="0 0 215 240"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        position: "absolute",
        zIndex: 1,
        left: 4,
        top: 4,
        width: svgWidth,
        height: svgHeight,
        overflow: "visible",
      }}
    >
      <motion.path
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0, duration: 1 }}
        d="M98.9805 3.36719C104.277 0.309242 110.765 0.213996 116.136 3.08105L116.651 3.36719L204.296 53.9688C209.763 57.1254 213.132 62.9601 213.132 69.2734V170.477C213.132 176.593 209.971 182.259 204.803 185.477L204.296 185.78L116.651 236.382C111.355 239.44 104.867 239.535 99.4961 236.668L98.9805 236.382L11.3359 185.78C5.8685 182.624 2.50011 176.79 2.5 170.477V69.2734C2.5 63.1573 5.66071 57.4905 10.8291 54.2725L11.3359 53.9688L98.9805 3.36719Z"
        fill="white"
        stroke={outlineColor}
        strokeWidth="2"
        className="parent-tile_shape-stroke"
      />
      {bgImg ? (
        <image
          href={bgImg}
          x="12"
          y="12"
          width={TILE_VARIANTS["large"].width - 24}
          height={TILE_VARIANTS["large"].height - 24}
          preserveAspectRatio="xMidYMid slice"
        />
      ) : (
        <motion.path
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0, duration: 1 }}
          d="M98.7823 14.2513C103.981 11.2496 110.387 11.2496 115.586 14.2513L194.467 59.7928C199.666 62.7946 202.869 68.3421 202.869 74.3457V165.429C202.869 171.432 199.666 176.98 194.467 179.981L115.586 225.523C110.387 228.525 103.981 228.525 98.7822 225.523L19.9021 179.981C14.7029 176.98 11.5 171.432 11.5 165.429V74.3457C11.5 68.3421 14.7029 62.7946 19.9021 59.7928L98.7823 14.2513Z"
          fill={bgColor}
        />
      )}
      <motion.path
        variants={draw}
        custom={13}
        d="M186.69 66.4104C185.781 67.0789 185.485 68.6786 185.559 69.7139C185.579 70.0022 185.401 70.2945 185.112 70.3118C182.661 70.4588 182.673 67.6196 183.358 66.0486C183.639 65.0905 185.607 62.8083 188.62 63.284C191.304 63.7077 191.772 66.5809 191.598 68.2661C191.578 68.4539 191.817 68.6067 191.978 68.5089C192.611 68.1257 193.797 67.5542 195.005 67.7431C198.478 68.1813 198.956 70.8521 198.537 73.1654C198.002 75.9313 195.522 77.839 192.954 76.5899C191.856 75.9231 191.848 75.163 193.677 74.24C194.805 73.6708 195.701 72.4163 194.773 71.2569C193.935 70.2937 192.608 70.8168 190.278 71.5601C188.245 72.2379 188.138 70.7753 188.395 70.0239C188.905 68.7063 188.976 68.1355 189.019 67.1537C189.03 66.1379 187.532 65.6563 186.69 66.4104Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={2}
        d="M177.948 75.4582C178.038 73.7923 180.016 73.6987 181.303 73.9008C181.497 73.9312 181.666 73.7544 181.598 73.5703C180.203 69.7712 174.503 72.3357 175.301 76.0236C176.126 79.8377 179.515 79.8421 181.337 79.0229C182.592 78.4585 182.658 77.6338 181.92 77.0563C181.671 76.8612 181.335 76.8486 181.024 76.9063C179.738 77.1446 177.859 77.1008 177.948 75.4582Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={3}
        d="M193.474 82.5496C191.82 83.6498 189.959 80.8242 189.047 78.7294C188.958 78.5248 189.229 78.3683 189.388 78.5248C190.63 79.7476 191.651 79.7765 192.654 79.7501C193.85 79.7186 194.785 81.3356 193.474 82.5496Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={4}
        d="M182.756 92.2144C185.65 92.5045 186.006 87.3259 185.686 84.1588C185.663 83.9367 185.352 83.9313 185.295 84.1469C184.582 86.8117 183.268 87.561 181.938 88.2212C180.441 88.9639 180.343 91.5722 182.756 92.2144Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={5}
        d="M180.751 112.699C182.851 112.081 183.874 115.717 184.131 118.19C184.154 118.411 183.849 118.478 183.746 118.28C182.84 116.537 181.771 116.171 180.702 115.871C179.451 115.519 179 113.538 180.751 112.699Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={6}
        d="M179.655 61.4374C176.854 65.5897 172.011 58.9042 170.016 55.6398C173.769 58.6007 174.748 56.9937 176.944 56.7918C178.855 56.6159 181.461 58.6825 179.655 61.4374Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={7}
        d="M190.484 99.543C195.484 98.7931 192.539 89.5356 190.158 85.6182C191.25 91.2411 188.818 92.1644 187.026 94.105C185.233 96.0455 186.559 99.9537 190.484 99.543Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={8}
        d="M151.081 46.1841C155.071 46.1571 163.535 48.1897 165.476 56.5357C167.102 66.2439 159.068 65.6445 158.146 62.1159C157.176 58.6384 161.651 58.8256 160.923 61.4943"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={9}
        d="M122.775 32.194C123.826 33.3563 126.657 35.5464 129.581 35.0087C133.236 34.3365 132.259 28.6204 128.711 29.7066C127.096 30.2007 127.384 31.9813 128.396 32.3322"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={10}
        d="M147.716 46.2663C149.788 46.0278 155.004 46.0158 156.996 49.4366C159.855 54.3479 152.976 56.2052 152.468 53.0398C152.112 50.8205 154.325 50.368 155.106 51.4276"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={11}
        d="M194.66 106.476C193.292 108.05 189.452 111.58 185.673 110.4C180.248 108.707 184.07 102.693 186.581 104.686C188.342 106.083 187.015 107.91 185.723 107.656"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={12}
        d="M172.648 62.5187C172.812 64.9308 172.543 70.9701 168.475 73.0908C162.633 76.1355 160.856 68.0706 164.548 67.6539C167.137 67.3619 167.541 69.9484 166.272 70.7956"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={12}
        d="M120.394 26.8697C121.521 30.4344 125.29 38.3057 131.34 41.273C138.903 44.9821 140.367 39.4705 137.883 37.8647C136.707 37.1048 135.427 37.6917 134.463 38.5588"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={12}
        d="M123.96 47.1083C123.835 49.0828 124.251 53.0658 126.923 53.2021C130.262 53.3724 129.324 48.9185 126.95 50.0402"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={1}
        d="M186.5 119.887L186.5 119.158C186.75 116.787 187.558 112.532 192.679 108.674C197.783 104.828 198.67 93.321 192.866 88.0787C189.18 84.7492 187.216 82.0648 186.376 80.5689C186.173 80.2069 185.605 80.1526 185.351 80.481C183.52 82.8493 179.396 86.4999 174.553 81.783C172.93 80.2312 170.882 75.9699 172.511 67.4131C174.549 56.717 164.813 49.3948 161.306 47.7526C157.565 46.0008 151.323 44.9003 142.461 47.447C133.598 49.9936 122.902 43.4741 121.272 30.7406C119.642 18.0072 109.816 20.8565 108.096 24.3895C106.932 26.8681 106.294 29.5372 108.158 32.6644C110.034 35.6056 113.722 34.6026 114.086 31.7436C114.321 29.8145 111.975 28.5042 110.899 29.5891C110.164 30.413 110.568 31.3027 111.102 31.6747"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={14}
        d="M123.042 37.4828C123.834 40.6615 125.366 48.7324 121.709 53.1322C116.146 58.3776 112.113 50.5121 116.044 48.4638C118.64 47.255 119.665 49.5137 119.505 50.6313C119.466 51.3681 118.632 52.483 117.377 51.6967"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={15}
        d="M188.209 113.615C188.895 112.219 190.985 109.585 193.853 110.218C197.624 111.224 196.189 116.613 192.501 115.014C191.387 114.478 191.687 113.013 192.969 112.907"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={16}
        d="M146.557 42.8559C147.919 45.9708 142.304 47.533 139.326 47.9248C142.348 46.4906 142.104 45.0331 142.36 43.3213C142.615 41.6094 145.155 40.6826 146.557 42.8559Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={17}
        d="M118.839 31.1115C121.439 30.4155 119.352 25.3897 118.123 23.3539C118.728 25.9316 117.987 27.2815 117.054 28.2853C116.121 29.2891 116.802 31.3184 118.839 31.1115Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={18}
        d="M142.755 53.12C144.249 50.0661 138.706 48.2657 135.748 47.7472C138.705 49.309 138.4 50.7548 138.582 52.476C138.764 54.1972 141.262 55.2316 142.755 53.12Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={19}
        d="M121.63 32.7213C122.241 36.0144 122.16 42.8858 116.945 44.0265C110.427 45.4524 113.262 36.3804 116.401 39.2028C117.514 40.4919 116.301 41.6541 115.52 41.5862"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.circle
        variants={draw}
        custom={2}
        cx="2.43015"
        cy="2.43015"
        r="1.63015"
        transform="matrix(4.37114e-08 1 1 -4.37114e-08 183.997 72.5521)"
        stroke={strokeColor}
        strokeWidth="1.6"
      />
      <motion.circle
        variants={draw}
        custom={2}
        cx="2.43015"
        cy="2.43015"
        r="1.63015"
        transform="matrix(4.37114e-08 1 1 -4.37114e-08 103.756 16.5357)"
        stroke={strokeColor}
        strokeWidth="1.6"
      />
      <motion.circle
        variants={draw}
        custom={2}
        cx="193.971"
        cy="119.887"
        r="2.03054"
        transform="rotate(90 193.971 119.887)"
        stroke={strokeColor}
        strokeWidth="1.6"
      />
      <motion.path
        variants={draw}
        custom={1}
        d="M186.69 173.364C185.781 172.695 185.485 171.096 185.559 170.06C185.579 169.772 185.401 169.48 185.112 169.462C182.661 169.315 182.673 172.155 183.358 173.725C183.639 174.684 185.607 176.966 188.62 176.49C191.304 176.066 191.772 173.193 191.598 171.508C191.578 171.32 191.817 171.167 191.978 171.265C192.611 171.648 193.797 172.22 195.005 172.031C198.478 171.593 198.956 168.922 198.537 166.609C198.002 163.843 195.522 161.935 192.954 163.184C191.856 163.851 191.848 164.611 193.677 165.534C194.805 166.103 195.701 167.358 194.773 168.517C193.935 169.48 192.608 168.957 190.278 168.214C188.245 167.536 188.138 168.999 188.395 169.75C188.905 171.068 188.976 171.639 189.019 172.62C189.03 173.636 187.532 174.118 186.69 173.364Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={2}
        d="M177.948 164.316C178.038 165.982 180.016 166.075 181.303 165.873C181.497 165.843 181.666 166.02 181.598 166.204C180.203 170.003 174.503 167.438 175.301 163.75C176.126 159.936 179.515 159.932 181.337 160.751C182.592 161.316 182.658 162.14 181.92 162.718C181.671 162.913 181.335 162.926 181.024 162.868C179.738 162.629 177.859 162.673 177.948 164.316Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={3}
        d="M193.474 157.225C191.82 156.124 189.959 158.95 189.047 161.045C188.958 161.249 189.229 161.406 189.388 161.249C190.63 160.026 191.651 159.998 192.654 160.024C193.85 160.055 194.785 158.439 193.474 157.225Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={4}
        d="M182.756 147.56C185.65 147.27 186.006 152.448 185.686 155.615C185.663 155.837 185.352 155.843 185.295 155.627C184.582 152.962 183.268 152.213 181.938 151.553C180.441 150.81 180.343 148.202 182.756 147.56Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={5}
        d="M180.751 127.075C182.851 127.693 183.874 124.057 184.131 121.585C184.154 121.363 183.849 121.296 183.746 121.494C182.84 123.237 181.771 123.603 180.702 123.903C179.451 124.255 179 126.236 180.751 127.075Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={6}
        d="M179.655 178.337C176.854 174.184 172.011 180.87 170.016 184.134C173.769 181.173 174.748 182.78 176.944 182.982C178.855 183.158 181.461 181.092 179.655 178.337Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={7}
        d="M190.484 140.231C195.484 140.981 192.539 150.239 190.158 154.156C191.25 148.533 188.818 147.61 187.026 145.669C185.233 143.729 186.559 139.82 190.484 140.231Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={8}
        d="M151.081 193.59C155.071 193.617 163.535 191.584 165.476 183.238C167.102 173.53 159.068 174.13 158.146 177.658C157.176 181.136 161.651 180.949 160.923 178.28"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={9}
        d="M122.775 207.58C123.826 206.418 126.657 204.228 129.581 204.765C133.236 205.438 132.259 211.154 128.711 210.068C127.096 209.573 127.384 207.793 128.396 207.442"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={10}
        d="M147.716 193.508C149.788 193.746 155.004 193.758 156.996 190.337C159.855 185.426 152.976 183.569 152.468 186.734C152.112 188.954 154.325 189.406 155.106 188.346"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={11}
        d="M194.66 133.298C193.292 131.724 189.452 128.194 185.673 129.374C180.248 131.067 184.07 137.081 186.581 135.088C188.342 133.691 187.015 131.864 185.723 132.118"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={12}
        d="M172.648 177.255C172.812 174.843 172.543 168.804 168.475 166.683C162.633 163.639 160.856 171.704 164.548 172.12C167.137 172.412 167.541 169.826 166.272 168.978"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={13}
        d="M120.394 212.904C121.521 209.34 125.29 201.468 131.34 198.501C138.903 194.792 140.367 200.304 137.883 201.909C136.707 202.669 135.427 202.082 134.463 201.215"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={14}
        d="M123.96 192.666C123.835 190.691 124.251 186.708 126.923 186.572C130.262 186.402 129.324 190.856 126.95 189.734"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={1}
        d="M186.5 119.887L186.5 120.616C186.75 122.988 187.558 127.242 192.679 131.101C197.783 134.946 198.67 146.453 192.866 151.695C189.18 155.025 187.216 157.709 186.376 159.205C186.173 159.567 185.605 159.622 185.351 159.293C183.52 156.925 179.396 153.274 174.553 157.991C172.93 159.543 170.882 163.804 172.511 172.361C174.549 183.057 164.813 190.379 161.306 192.021C157.565 193.773 151.323 194.874 142.461 192.327C133.598 189.78 122.902 196.3 121.272 209.033C119.642 221.767 109.816 218.918 108.096 215.385C106.932 212.906 106.294 210.237 108.158 207.11C110.034 204.169 113.722 205.171 114.086 208.031C114.321 209.96 111.975 211.27 110.899 210.185C110.164 209.361 110.568 208.471 111.102 208.099"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={15}
        d="M123.042 202.291C123.834 199.113 125.366 191.042 121.709 186.642C116.146 181.397 112.113 189.262 116.044 191.31C118.64 192.519 119.665 190.26 119.505 189.143C119.466 188.406 118.632 187.291 117.377 188.077"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={16}
        d="M188.209 126.159C188.895 127.555 190.985 130.189 193.853 129.556C197.624 128.551 196.189 123.161 192.501 124.76C191.387 125.296 191.687 126.761 192.969 126.867"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={17}
        d="M146.557 196.918C147.919 193.803 142.304 192.241 139.326 191.849C142.348 193.283 142.104 194.741 142.36 196.453C142.615 198.165 145.155 199.092 146.557 196.918Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={18}
        d="M118.839 208.663C121.439 209.359 119.352 214.384 118.123 216.42C118.728 213.843 117.987 212.493 117.054 211.489C116.121 210.485 116.802 208.456 118.839 208.663Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={19}
        d="M142.755 186.654C144.249 189.708 138.706 191.508 135.748 192.027C138.705 190.465 138.4 189.019 138.582 187.298C138.764 185.577 141.262 184.543 142.755 186.654Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={20}
        d="M121.63 207.053C122.241 203.76 122.16 196.888 116.945 195.748C110.427 194.322 113.262 203.394 116.401 200.571C117.514 199.282 116.301 198.12 115.52 198.188"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.circle
        variants={draw}
        custom={2}
        cx="186.427"
        cy="164.792"
        r="1.63015"
        transform="rotate(-90 186.427 164.792)"
        stroke={strokeColor}
        strokeWidth="1.6"
      />
      <motion.circle
        variants={draw}
        custom={2}
        cx="106.186"
        cy="220.808"
        r="1.63015"
        transform="rotate(-90 106.186 220.808)"
        stroke={strokeColor}
        strokeWidth="1.6"
      />
      <motion.circle
        variants={draw}
        custom={2}
        cx="2.83054"
        cy="2.83054"
        r="2.03054"
        transform="matrix(4.37114e-08 -1 -1 -4.37114e-08 196.802 122.718)"
        stroke={strokeColor}
        strokeWidth="1.6"
      />
      <motion.path
        variants={draw}
        custom={1}
        d="M25.6822 66.4104C26.591 67.0789 26.887 68.6786 26.8132 69.7139C26.7927 70.0022 26.9711 70.2945 27.2596 70.3118C29.711 70.4588 29.6989 67.6196 29.0142 66.0486C28.7334 65.0905 26.7652 62.8083 23.7522 63.284C21.0685 63.7077 20.5996 66.5809 20.7744 68.2661C20.7938 68.4539 20.5555 68.6067 20.3939 68.5089C19.7608 68.1257 18.5746 67.5542 17.3668 67.7431C13.894 68.1813 13.4161 70.8521 13.8354 73.1654C14.3699 75.9313 16.8499 77.839 19.4182 76.5899C20.5159 75.9231 20.524 75.163 18.6949 74.24C17.5669 73.6708 16.6714 72.4163 17.5988 71.2569C18.4372 70.2937 19.7645 70.8168 22.0936 71.5601C24.1269 72.2379 24.234 70.7753 23.9767 70.0239C23.467 68.7063 23.3956 68.1355 23.3531 67.1537C23.3421 66.1379 24.8404 65.6563 25.6822 66.4104Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={1}
        d="M34.4239 75.4582C34.3337 73.7923 32.3561 73.6987 31.0687 73.9008C30.8749 73.9312 30.7063 73.7544 30.7739 73.5703C32.1688 69.7712 37.8693 72.3357 37.0714 76.0236C36.2462 79.8377 32.8574 79.8421 31.0354 79.0229C29.78 78.4585 29.7144 77.6338 30.4521 77.0563C30.7013 76.8612 31.0368 76.8486 31.348 76.9063C32.6344 77.1446 34.5129 77.1008 34.4239 75.4582Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={2}
        d="M18.8976 82.5496C20.5521 83.6498 22.4131 80.8242 23.3248 78.7294C23.4139 78.5248 23.1433 78.3683 22.9843 78.5248C21.7422 79.7476 20.7213 79.7765 19.7176 79.7501C18.5222 79.7186 17.5869 81.3356 18.8976 82.5496Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={3}
        d="M29.6166 92.2144C26.7226 92.5045 26.3657 87.3259 26.6864 84.1588C26.7089 83.9367 27.0198 83.9313 27.0775 84.1469C27.7901 86.8117 29.1039 87.561 30.4343 88.2212C31.931 88.9639 32.0288 91.5722 29.6166 92.2144Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={4}
        d="M31.6206 112.699C29.5206 112.081 28.4977 115.717 28.2415 118.19C28.2185 118.411 28.5232 118.478 28.6261 118.28C29.5324 116.537 30.6012 116.171 31.6699 115.871C32.9215 115.519 33.3726 113.538 31.6206 112.699Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={5}
        d="M32.7169 61.4374C35.5177 65.5897 40.3609 58.9042 42.3564 55.6398C38.6027 58.6007 37.6239 56.9937 35.4282 56.7918C33.5167 56.6159 30.9107 58.6825 32.7169 61.4374Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={6}
        d="M21.8882 99.543C16.8882 98.7931 19.8333 89.5356 22.2138 85.6182C21.1218 91.2411 23.5542 92.1644 25.3466 94.105C27.139 96.0455 25.8135 99.9537 21.8882 99.543Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={7}
        d="M61.291 46.1841C57.3014 46.1571 48.8368 48.1897 46.8959 56.5357C45.2698 66.2439 53.304 65.6445 54.2257 62.1159C55.1961 58.6384 50.7212 58.8256 51.4491 61.4943"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={8}
        d="M89.5967 32.194C88.5464 33.3563 85.715 35.5464 82.7911 35.0087C79.1363 34.3365 80.1127 28.6204 83.6616 29.7066C85.2758 30.2007 84.9884 31.9813 83.9756 32.3322"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={9}
        d="M64.6559 46.2663C62.5838 46.0278 57.3682 46.0158 55.3764 49.4366C52.5169 54.3479 59.3957 56.2052 59.9036 53.0398C60.2598 50.8205 58.0469 50.368 57.2656 51.4276"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={10}
        d="M17.7124 106.476C19.0803 108.05 22.9204 111.58 26.699 110.4C32.1239 108.707 28.3022 102.693 25.7908 104.686C24.03 106.083 25.3572 107.91 26.649 107.656"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={11}
        d="M39.7241 62.5187C39.5601 64.9308 39.8289 70.9701 43.8975 73.0908C49.7387 76.1355 51.5164 68.0706 47.8239 67.6539C45.2351 67.3619 44.8311 69.9484 46.1003 70.7956"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={12}
        d="M91.9785 26.8697C90.8507 30.4344 87.0824 38.3057 81.0321 41.273C73.4691 44.9821 72.0055 39.4705 74.4895 37.8647C75.6648 37.1048 76.9453 37.6917 77.9087 38.5588"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={13}
        d="M88.4118 47.1083C88.5375 49.0828 88.1209 53.0658 85.4492 53.2021C82.1096 53.3724 83.0479 48.9185 85.4221 50.0402"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={1}
        d="M25.8719 119.887L25.8719 119.158C25.6223 116.787 24.8145 112.532 19.6935 108.674C14.5895 104.828 13.7023 93.321 19.5063 88.0787C23.1925 84.7492 25.1559 82.0648 25.9959 80.5689C26.1992 80.2069 26.7675 80.1526 27.0215 80.481C28.8524 82.8493 32.9765 86.4999 37.8193 81.783C39.4417 80.2312 41.4906 75.9699 39.8607 67.4131C37.8233 56.717 47.5592 49.3948 51.0661 47.7526C54.8072 46.0008 61.0491 44.9003 69.9116 47.447C78.774 49.9936 89.4701 43.4741 91.1 30.7406C92.7299 18.0072 102.556 20.8565 104.276 24.3895C105.44 26.8681 106.078 29.5372 104.214 32.6644C102.338 35.6056 98.6497 34.6026 98.2861 31.7436C98.0512 29.8145 100.397 28.5042 101.473 29.5891C102.209 30.413 101.804 31.3027 101.27 31.6747"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={14}
        d="M89.3303 37.4828C88.5377 40.6615 87.0062 48.7324 90.6626 53.1322C96.2264 58.3776 100.259 50.5121 96.3284 48.4638C93.7324 47.255 92.7069 49.5137 92.8669 50.6313C92.9064 51.3681 93.7397 52.483 94.9955 51.6967"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={15}
        d="M24.1632 113.615C23.4766 112.219 21.3866 109.585 18.5192 110.218C14.7479 111.224 16.1832 116.613 19.8712 115.014C20.985 114.478 20.6847 113.013 19.4028 112.907"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={16}
        d="M65.8155 42.8559C64.4531 45.9708 70.0679 47.533 73.0456 47.9248C70.0244 46.4906 70.2677 45.0331 70.0122 43.3213C69.7566 41.6094 67.2167 40.6826 65.8155 42.8559Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={17}
        d="M93.5329 31.1115C90.9328 30.4155 93.0203 25.3897 94.249 23.3539C93.6437 25.9316 94.3855 27.2815 95.3183 28.2853C96.2511 29.2891 95.5697 31.3184 93.5329 31.1115Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={18}
        d="M69.6173 53.12C68.1232 50.0661 73.6662 48.2657 76.6244 47.7472C73.6672 49.309 73.9725 50.7548 73.7902 52.476C73.6079 54.1972 71.1098 55.2316 69.6173 53.12Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={19}
        d="M90.7421 32.7213C90.1311 36.0144 90.2125 42.8858 95.4272 44.0265C101.946 45.4524 99.1102 36.3804 95.9712 39.2028C94.8585 40.4919 96.0708 41.6541 96.8517 41.5862"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.circle
        variants={draw}
        custom={2}
        cx="25.9449"
        cy="74.9823"
        r="1.63015"
        transform="rotate(90 25.9449 74.9823)"
        stroke={strokeColor}
        strokeWidth="1.6"
      />
      <motion.circle
        variants={draw}
        custom={2}
        cx="106.186"
        cy="18.9658"
        r="1.63015"
        transform="rotate(90 106.186 18.9658)"
        stroke={strokeColor}
        strokeWidth="1.6"
      />
      <motion.circle
        variants={draw}
        custom={2}
        cx="2.83054"
        cy="2.83054"
        r="2.03054"
        transform="matrix(-4.37114e-08 1 1 4.37114e-08 15.5703 117.056)"
        stroke={strokeColor}
        strokeWidth="1.6"
      />
      <motion.path
        variants={draw}
        custom={1}
        d="M25.6822 173.364C26.591 172.695 26.887 171.096 26.8132 170.06C26.7927 169.772 26.9711 169.48 27.2596 169.462C29.711 169.315 29.6989 172.155 29.0142 173.725C28.7334 174.684 26.7652 176.966 23.7522 176.49C21.0685 176.066 20.5996 173.193 20.7744 171.508C20.7938 171.32 20.5555 171.167 20.3939 171.265C19.7608 171.648 18.5746 172.22 17.3668 172.031C13.894 171.593 13.4161 168.922 13.8354 166.609C14.3699 163.843 16.8499 161.935 19.4182 163.184C20.5159 163.851 20.524 164.611 18.6949 165.534C17.5669 166.103 16.6714 167.358 17.5988 168.517C18.4372 169.48 19.7645 168.957 22.0936 168.214C24.1269 167.536 24.234 168.999 23.9767 169.75C23.467 171.068 23.3956 171.639 23.3531 172.62C23.3421 173.636 24.8404 174.118 25.6822 173.364Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={2}
        d="M34.4239 164.316C34.3337 165.982 32.3561 166.075 31.0687 165.873C30.8749 165.843 30.7063 166.02 30.7739 166.204C32.1688 170.003 37.8693 167.438 37.0714 163.75C36.2462 159.936 32.8574 159.932 31.0354 160.751C29.78 161.316 29.7144 162.14 30.4521 162.718C30.7013 162.913 31.0368 162.926 31.348 162.868C32.6344 162.629 34.5129 162.673 34.4239 164.316Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={3}
        d="M18.8976 157.225C20.5521 156.124 22.4131 158.95 23.3248 161.045C23.4139 161.249 23.1433 161.406 22.9843 161.249C21.7422 160.026 20.7213 159.998 19.7176 160.024C18.5222 160.055 17.5869 158.439 18.8976 157.225Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={4}
        d="M29.6166 147.56C26.7226 147.27 26.3657 152.448 26.6864 155.615C26.7089 155.837 27.0198 155.843 27.0775 155.627C27.7901 152.962 29.1039 152.213 30.4343 151.553C31.931 150.81 32.0288 148.202 29.6166 147.56Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={5}
        d="M31.6206 127.075C29.5206 127.693 28.4977 124.057 28.2415 121.585C28.2185 121.363 28.5232 121.296 28.6261 121.494C29.5324 123.237 30.6012 123.603 31.6699 123.903C32.9215 124.255 33.3726 126.236 31.6206 127.075Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={6}
        d="M32.7169 178.337C35.5177 174.184 40.3609 180.87 42.3564 184.134C38.6027 181.173 37.6239 182.78 35.4282 182.982C33.5167 183.158 30.9107 181.092 32.7169 178.337Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={7}
        d="M21.8882 140.231C16.8882 140.981 19.8333 150.239 22.2138 154.156C21.1218 148.533 23.5542 147.61 25.3466 145.669C27.139 143.729 25.8135 139.82 21.8882 140.231Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={8}
        d="M61.291 193.59C57.3014 193.617 48.8368 191.584 46.8959 183.238C45.2698 173.53 53.304 174.13 54.2257 177.658C55.1961 181.136 50.7212 180.949 51.4491 178.28"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={9}
        d="M89.5967 207.58C88.5464 206.418 85.715 204.228 82.7911 204.765C79.1363 205.438 80.1127 211.154 83.6616 210.068C85.2758 209.573 84.9884 207.793 83.9756 207.442"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={10}
        d="M64.6559 193.508C62.5838 193.746 57.3682 193.758 55.3764 190.337C52.5169 185.426 59.3957 183.569 59.9036 186.734C60.2598 188.954 58.0469 189.406 57.2656 188.346"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={11}
        d="M17.7124 133.298C19.0803 131.724 22.9204 128.194 26.699 129.374C32.1239 131.067 28.3022 137.081 25.7908 135.088C24.03 133.691 25.3572 131.864 26.649 132.118"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={12}
        d="M39.7241 177.255C39.5601 174.843 39.8289 168.804 43.8975 166.683C49.7387 163.639 51.5164 171.704 47.8239 172.12C45.2351 172.412 44.8311 169.826 46.1003 168.978"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={13}
        d="M91.9785 212.904C90.8507 209.34 87.0824 201.468 81.0321 198.501C73.4691 194.792 72.0055 200.304 74.4895 201.909C75.6648 202.669 76.9453 202.082 77.9087 201.215"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={14}
        d="M88.4118 192.666C88.5375 190.691 88.1209 186.708 85.4492 186.572C82.1096 186.402 83.0479 190.856 85.4221 189.734"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={1}
        d="M25.8719 119.887L25.8719 120.616C25.6223 122.988 24.8145 127.242 19.6935 131.101C14.5895 134.946 13.7023 146.453 19.5063 151.695C23.1925 155.025 25.1559 157.709 25.9959 159.205C26.1992 159.567 26.7675 159.622 27.0215 159.293C28.8524 156.925 32.9765 153.274 37.8193 157.991C39.4417 159.543 41.4906 163.804 39.8607 172.361C37.8233 183.057 47.5592 190.379 51.0661 192.021C54.8072 193.773 61.0491 194.874 69.9116 192.327C78.774 189.78 89.4701 196.3 91.1 209.033C92.7299 221.767 102.556 218.918 104.276 215.385C105.44 212.906 106.078 210.237 104.214 207.11C102.338 204.169 98.6497 205.171 98.2861 208.031C98.0512 209.96 100.397 211.27 101.473 210.185C102.209 209.361 101.804 208.471 101.27 208.099"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={15}
        d="M89.3303 202.291C88.5377 199.113 87.0062 191.042 90.6626 186.642C96.2264 181.397 100.259 189.262 96.3284 191.31C93.7324 192.519 92.7069 190.26 92.8669 189.143C92.9064 188.406 93.7397 187.291 94.9955 188.077"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={16}
        d="M24.1632 126.159C23.4766 127.555 21.3866 130.189 18.5192 129.556C14.7479 128.551 16.1832 123.161 19.8712 124.76C20.985 125.296 20.6847 126.761 19.4028 126.867"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.path
        variants={draw}
        custom={17}
        d="M65.8155 196.918C64.4531 193.803 70.0679 192.241 73.0456 191.849C70.0244 193.283 70.2677 194.741 70.0122 196.453C69.7566 198.165 67.2167 199.092 65.8155 196.918Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={18}
        d="M93.5329 208.663C90.9328 209.359 93.0203 214.384 94.249 216.42C93.6437 213.843 94.3855 212.493 95.3183 211.489C96.2511 210.485 95.5697 208.456 93.5329 208.663Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={19}
        d="M69.6173 186.654C68.1232 189.708 73.6662 191.508 76.6244 192.027C73.6672 190.465 73.9725 189.019 73.7902 187.298C73.6079 185.577 71.1098 184.543 69.6173 186.654Z"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      <motion.path
        variants={draw}
        custom={20}
        d="M90.7421 207.053C90.1311 203.76 90.2125 196.888 95.4272 195.748C101.946 194.322 99.1102 203.394 95.9712 200.571C94.8585 199.282 96.0708 198.12 96.8517 198.188"
        stroke={strokeColor}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <motion.circle
        variants={draw}
        custom={2}
        cx="2.43015"
        cy="2.43015"
        r="1.63015"
        transform="matrix(-4.37114e-08 -1 -1 4.37114e-08 28.375 167.222)"
        stroke={strokeColor}
        strokeWidth="1.6"
      />
      <motion.circle
        variants={draw}
        custom={2}
        cx="2.43015"
        cy="2.43015"
        r="1.63015"
        transform="matrix(-4.37114e-08 -1 -1 4.37114e-08 108.616 223.238)"
        stroke={strokeColor}
        strokeWidth="1.6"
      />
      <motion.circle
        variants={draw}
        custom={2}
        cx="18.4009"
        cy="119.887"
        r="2.03054"
        transform="rotate(-90 18.4009 119.887)"
        stroke={strokeColor}
        strokeWidth="1.6"
      />
      {!hideTopGlyph ? (
        <motion.path
          variants={draw}
          custom={15}
          d="M106.769 68.7V90.3M117.573 79.4959L95.9727 79.4959M114.413 87.1339L99.1395 71.8604M99.1394 87.1396L114.413 71.8661"
          stroke={strokeColor}
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      ) : null}
    </motion.svg>
  );
}
